import type {
  AssignTechnicianInput,
  AuthResponse,
  ConfirmCardPaymentInput,
  CreateAppointmentInput,
  CreateReportInput,
  CreateStaffInput,
  DocumentRecord,
  DocumentStatus,
  Payment,
  Project,
  ProjectStatus,
  PublicUser,
  RequestReportUploadInput,
  ReviewDocumentInput,
  SubmitReportInput,
  UpdateClientInput,
  UpdateProjectStatusInput,
  UpdateStaffInput,
  UpdateUserInput,
  UserTypeFilter,
  ValidateBankTransferInput,
} from '@bcl/shared';
import { api } from '@/lib/api';

export type TimelineEvent = {
  id: string;
  status: ProjectStatus;
  message: string | null;
  actorId: string | null;
  createdAt: string;
};

export type ClientDetail = {
  client: PublicUser;
  projects: Project[];
};

export type Appointment = {
  id: string;
  projectId: string;
  technicianId: string;
  scheduledAt: string;
  durationMinutes: number;
  notes: string | null;
  createdAt: string;
};

export type AdminStats = {
  clients: number;
  staff: number;
  projects: number;
  projectsByStatus: Record<string, number>;
  pendingLeads: number;
  pendingDocuments: number;
  upcomingAppointments: number;
  cumulativeRevenue: number;
};

export type Report = {
  id: string;
  projectId: string;
  authorId: string;
  title: string;
  content: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
};

export async function getStats(): Promise<AdminStats> {
  const { data } = await api.get<AdminStats>('/admin/stats');
  return data;
}

export async function listClients(): Promise<PublicUser[]> {
  const { data } = await api.get<PublicUser[]>('/admin/clients');
  return data;
}

export async function listAllProjects(status?: ProjectStatus): Promise<Project[]> {
  const { data } = await api.get<Project[]>('/admin/projects', {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function assignTechnician(
  projectId: string,
  input: AssignTechnicianInput,
): Promise<Project> {
  const { data } = await api.post<Project>(
    `/admin/projects/${projectId}/assign-technician`,
    input,
  );
  return data;
}

export async function listAdminDocuments(
  status: DocumentStatus = 'en_attente',
): Promise<DocumentRecord[]> {
  const { data } = await api.get<DocumentRecord[]>('/admin/documents', {
    params: { status },
  });
  return data;
}

export async function createAppointment(
  input: CreateAppointmentInput,
): Promise<Appointment> {
  const { data } = await api.post<Appointment>('/admin/appointments', input);
  return data;
}

export async function listTechnicianAppointments(): Promise<Appointment[]> {
  const { data } = await api.get<Appointment[]>(
    '/admin/technician/appointments',
  );
  return data;
}

export async function listTechnicianProjects(): Promise<Project[]> {
  const { data } = await api.get<Project[]>('/admin/technician/projects');
  return data;
}

export async function listTechnicianDocuments(): Promise<DocumentRecord[]> {
  const { data } = await api.get<DocumentRecord[]>('/admin/technician/documents');
  return data;
}

export async function listTechnicianReports(): Promise<Report[]> {
  const { data } = await api.get<Report[]>('/admin/technician/reports');
  return data;
}

export async function createReport(input: CreateReportInput): Promise<Report> {
  const { data } = await api.post<Report>('/admin/reports', input);
  return data;
}

/** Étape 1 du dépôt de compte rendu : URL pré-signée pour le PDF (upload direct S3). */
export async function requestReportUploadUrl(
  projectId: string,
  input: RequestReportUploadInput,
): Promise<{ fileS3Key: string; uploadUrl: string }> {
  const { data } = await api.post<{ fileS3Key: string; uploadUrl: string }>(
    `/admin/technician/projects/${projectId}/report-upload-url`,
    input,
  );
  return data;
}

/** Étape 2 : soumission du compte rendu (PDF déjà uploadé + note WYSIWYG optionnelle). */
export async function submitTechnicianReport(
  projectId: string,
  input: SubmitReportInput,
): Promise<Report> {
  const { data } = await api.post<Report>(
    `/admin/technician/projects/${projectId}/report`,
    input,
  );
  return data;
}

/** URL pré-signée de téléchargement du PDF d'un compte rendu. */
export async function getReportFileUrl(reportId: string): Promise<string> {
  const { data } = await api.get<{ url: string }>(
    `/admin/technician/reports/${reportId}/file-url`,
  );
  return data.url;
}

export async function markInterventionRealised(projectId: string): Promise<Project> {
  const { data } = await api.post<Project>(
    `/admin/technician/projects/${projectId}/mark-realised`,
  );
  return data;
}

// --- Notifications back-office (fil des étapes de dossier) ---

export type AdminNotification = {
  id: string;
  projectId: string;
  reference: string | null;
  status: ProjectStatus;
  message: string | null;
  createdAt: string;
};

export async function listNotifications(): Promise<AdminNotification[]> {
  const { data } = await api.get<AdminNotification[]>('/admin/notifications');
  return data;
}

// --- Gestion unifiée des utilisateurs (super_admin) ---

export async function listAllUsers(type: UserTypeFilter): Promise<PublicUser[]> {
  const { data } = await api.get<PublicUser[]>('/admin/users', { params: { type } });
  return data;
}

export async function updateUser(
  userId: string,
  input: UpdateUserInput,
): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>(`/admin/users/${userId}`, input);
  return data;
}

export async function deactivateUser(userId: string): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>(`/admin/users/${userId}/deactivate`);
  return data;
}

export async function reactivateUser(userId: string): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>(`/admin/users/${userId}/reactivate`);
  return data;
}

export async function impersonateUser(userId: string): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>(`/admin/users/${userId}/impersonate`);
  return data;
}

export async function listPaymentsByStatus(
  status: 'en_attente' | 'paye',
): Promise<Payment[]> {
  const { data } = await api.get<Payment[]>('/admin/payments', {
    params: { status },
  });
  return data;
}

export async function reviewDocument(
  documentId: string,
  input: ReviewDocumentInput,
) {
  const { data } = await api.post(`/documents/${documentId}/review`, input);
  return data;
}

export async function confirmCardPayment(input: ConfirmCardPaymentInput) {
  const { data } = await api.post<Payment>('/payments/confirm-card', input);
  return data;
}

export async function validateBankTransfer(input: ValidateBankTransferInput) {
  const { data } = await api.post<Payment>('/payments/validate-transfer', input);
  return data;
}

// --- Super-admin only : équipe BCL ---

export async function listStaff(): Promise<PublicUser[]> {
  const { data } = await api.get<PublicUser[]>('/admin/staff');
  return data;
}

export async function createStaff(input: CreateStaffInput): Promise<PublicUser> {
  const { data } = await api.post<PublicUser>('/admin/staff', input);
  return data;
}

export async function updateStaff(
  userId: string,
  input: UpdateStaffInput,
): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>(`/admin/staff/${userId}`, input);
  return data;
}

export async function deleteStaff(userId: string): Promise<void> {
  await api.delete(`/admin/staff/${userId}`);
}

export async function deactivateStaff(userId: string): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>(
    `/admin/staff/${userId}/deactivate`,
  );
  return data;
}

export async function reactivateStaff(userId: string): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>(
    `/admin/staff/${userId}/reactivate`,
  );
  return data;
}

// --- Détail dossier admin ---

export async function getProjectDetail(projectId: string): Promise<Project> {
  const { data } = await api.get<Project>(`/admin/projects/${projectId}`);
  return data;
}

export async function getProjectTimeline(
  projectId: string,
): Promise<TimelineEvent[]> {
  const { data } = await api.get<TimelineEvent[]>(
    `/admin/projects/${projectId}/timeline`,
  );
  return data;
}

export async function getProjectDocuments(
  projectId: string,
): Promise<DocumentRecord[]> {
  const { data } = await api.get<DocumentRecord[]>(
    `/admin/projects/${projectId}/documents`,
  );
  return data;
}

export async function getProjectPayments(
  projectId: string,
): Promise<Payment[]> {
  const { data } = await api.get<Payment[]>(
    `/admin/projects/${projectId}/payments`,
  );
  return data;
}

export async function updateProjectStatus(
  projectId: string,
  input: UpdateProjectStatusInput,
): Promise<Project> {
  const { data } = await api.patch<Project>(
    `/admin/projects/${projectId}/status`,
    input,
  );
  return data;
}

export async function listTechnicians(): Promise<PublicUser[]> {
  const { data } = await api.get<PublicUser[]>('/admin/technicians');
  return data;
}

export async function listReportsForProject(
  projectId: string,
): Promise<Report[]> {
  const { data } = await api.get<Report[]>(
    `/admin/reports/by-project/${projectId}`,
  );
  return data;
}

// --- Détail client admin ---

export async function getClientDetail(
  clientId: string,
): Promise<ClientDetail> {
  const { data } = await api.get<ClientDetail>(`/admin/clients/${clientId}`);
  return data;
}

export async function updateClient(
  clientId: string,
  input: UpdateClientInput,
): Promise<PublicUser> {
  const { data } = await api.patch<PublicUser>(
    `/admin/clients/${clientId}`,
    input,
  );
  return data;
}
